import axios from 'axios';
import { createContext, useContext, useReducer } from 'react';
import { conversationReducer } from '../reducers/conversationReducer';
import {
  apiUrl,
  GET_CONVERSATION_DETAIL_SUCCESS,
  LOAD_SUCCESS,
  PushEventEnum,
  UPDATE_CONVERSATION_SUCCESS,
} from '../utils/constants';
import { AuthContext } from './AuthContext';

export const ConversationContext = createContext();
export let userIdChannel;

const ConversationContextProvider = ({ children }) => {
  const {
    authState: { user },
  } = useContext(AuthContext);

  const [conversationState, dispatch] = useReducer(conversationReducer, {
    conversationLoading: true,
    conversations: [],
    conversation: {
      items: [],
      pagination: null,
    },
  });

  const subscribeChannel = () => {
    userIdChannel = new EventSource(`${apiUrl}/sse/${user?.id}`);
    userIdChannel.onopen = () => {
      console.log(`userIdChannel ${user?.id} connected`);
    };
    userIdChannel.onmessage = (e) => {
      const data = JSON.parse(e?.data);
      switch (data?.type) {
        case PushEventEnum.CONVERSATION_GROUP_JOINED:
          console.log(`userIdChannel ${user?.id}`, data?.type, data?.data);
          dispatch({
            type: PushEventEnum.CONVERSATION_GROUP_JOINED,
            payload: {
              conversation: data?.data?.conversation,
              userIds: data?.data?.userIds,
              authUserId: user?.id,
            },
          });
          break;

        case PushEventEnum.NEW_CONVERSATION:
          console.log(`userIdChannel ${user?.id}`, data?.type, data?.data);
          dispatch({
            type: PushEventEnum.NEW_CONVERSATION,
            payload: {
              conversation: data?.data?.conversation,
            },
          });
          break;

        case PushEventEnum.CONVERSATION_GROUP_CREATED:
          console.log(`userIdChannel ${user?.id}`, data?.type, data?.data);
          dispatch({
            type: PushEventEnum.NEW_CONVERSATION,
            payload: {
              conversation: data?.data?.conversation,
            },
          });
          break;

        case PushEventEnum.CONVERSATION_UPDATED:
          console.log(`userIdChannel ${user?.id}`, data?.type, data?.data);
          dispatch({
            type: UPDATE_CONVERSATION_SUCCESS,
            payload: {
              conversation: data?.data.conversation,
              message: data?.data.message,
            },
          });
          break;

        case PushEventEnum.CONVERSATION_GROUP_LEFT:
          console.log(`userIdChannel ${user?.id}`, data?.type, data?.data);
          dispatch({
            type: PushEventEnum.CONVERSATION_GROUP_LEFT,
            payload: {
              conversation: data?.data?.conversation,
              userId: data?.data?.userId,
              authUserId: user?.id,
            },
          });
          break;

        case PushEventEnum.NEW_MESSAGE:
          console.log(`userIdChannel ${user?.id}`, data?.type, data?.data);
          dispatch({
            type: PushEventEnum.NEW_MESSAGE,
            payload: {
              conversation: data?.data?.conversation,
              message: data?.data?.message,
            },
          });
          break;

        case PushEventEnum.MESSAGE_SEEN:
          console.log(`userIdChannel ${user?.id}`, data?.type, data?.data);
          dispatch({
            type: PushEventEnum.MESSAGE_SEEN,
            payload: {
              messages: data?.data?.messages,
            },
          });
          break;

        default:
          break;
      }
    };
  };

  const getConversations = async () => {
    try {
      const response = await axios.get(`${apiUrl}/chat/me`);
      if (response?.status === 200) {
        dispatch({
          type: LOAD_SUCCESS,
          payload: {
            conversations: response?.data?.data,
          },
        });
        subscribeChannel();
      }
      return response?.data;
    } catch (error) {
      if (error?.response?.data) return error?.response?.data;
      else return { success: false, message: error?.message };
    }
  };

  const getConversationDetail = async (conversationId) => {
    try {
      let response;
      if (
        !conversationState?.conversation?.pagination ||
        conversationState?.conversation?.pagination?.page !==
          conversationState?.conversation?.pagination?.totalPages ||
        conversationId !==
          conversationState?.conversation?.items[0]?.conversationId
      ) {
        response = await axios.get(
          `${apiUrl}/conversations/${conversationId}?page=${
            conversationState?.conversation
              ? conversationId ===
                conversationState?.conversation?.items[0]?.conversationId
                ? Number(conversationState?.conversation?.pagination?.page) + 1
                : 1
              : 1
          }`
        );
      }
      if (response?.status === 200) {
        dispatch({
          type: GET_CONVERSATION_DETAIL_SUCCESS,
          payload: {
            conversation: {
              items: response?.data?.data?.items,
              pagination: response?.data?.data?.pagination,
            },
          },
        });
      }

      return response?.data;
    } catch (error) {
      console.log(error);
      if (error?.response?.data) return error?.response?.data;
      else return { success: false, message: error?.message };
    }
  };

  const updateConversation = async (conversationId, payload) => {
    try {
      const response = await axios.put(
        `${apiUrl}/conversations/${conversationId}`,
        payload
      );

      return response?.data;
    } catch (error) {
      if (error?.response?.data) return error?.response?.data;
      else return { success: false, message: error?.message };
    }
  };

  const removeUserFromGroupChat = async (conversationId, userId) => {
    try {
      const response = await axios.delete(
        `${apiUrl}/conversations/${conversationId}/users/${userId}`
      );

      return response?.data;
    } catch (error) {
      if (error?.response?.data) return error?.response?.data;
      else return { success: false, message: error?.message };
    }
  };

  const addUserToGroupChat = async (conversationId, userId) => {
    try {
      const response = await axios.post(
        `${apiUrl}/conversations/${conversationId}/users`,
        { userIds: [userId] }
      );

      return response?.data;
    } catch (error) {
      if (error?.response?.data) return error?.response?.data;
      else return { success: false, message: error?.message };
    }
  };

  const sendNewMessage = async (messageForm) => {
    try {
      const response = await axios.post(`${apiUrl}/chat`, messageForm);
      return response?.data;
    } catch (error) {
      if (error?.response?.data) return error?.response?.data;
      else return { success: false, message: error?.message };
    }
  };

  const createGroupChat = async (messageForm) => {
    try {
      const response = await axios.post(`${apiUrl}/chat/group`, messageForm);
      return response?.data;
    } catch (error) {
      if (error?.response?.data) return error?.response?.data;
      else return { success: false, message: error?.message };
    }
  };

  const markSeenMessages = async (conversationId, items) => {
    try {
      if (items?.length > 0) {
        const find = items?.find((item) => {
          const userSeenIds = item?.usersSeen?.map((userSeen) => userSeen?.id);
          return !userSeenIds?.includes(user.id);
        });
        if (find) {
          await axios.post(
            `${apiUrl}/conversations/${conversationId}/messages/mark-seen`,
            { messageIds: items?.map((item) => item.id) }
          );
        }
      }
    } catch (error) {
      console.log(error);
      if (error?.response?.data) return error?.response?.data;
      else return { success: false, message: error?.message };
    }
  };

  const conversationContextData = {
    getConversations,
    conversationState,
    getConversationDetail,
    updateConversation,
    removeUserFromGroupChat,
    addUserToGroupChat,
    sendNewMessage,
    createGroupChat,
    markSeenMessages,
  };

  return (
    <ConversationContext.Provider value={conversationContextData}>
      {children}
    </ConversationContext.Provider>
  );
};

export default ConversationContextProvider;
